import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  AlertCircle,
  Upload,
  FileText,
  Loader2,
  Info,
  X
} from 'lucide-react';
import { 
  PCOSQuizData, 
  QUIZ_SECTIONS,
  DemographicsData,
  MenstrualData,
  HyperandrogenicData,
  MetabolicData,
  MentalHealthData,
  InflammatoryData,
  FamilyHistoryData,
  LifestyleData,
  GLP1ScreeningData,
  NimCoreLabPanel,
} from '@/lib/pcos/types';
import { calculatePCOSAssessment } from '@/lib/pcos/scoring-algorithm';

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialDemographics: DemographicsData = {
  ageRange: '',
  ethnicity: '',
  heightCm: 0,
  weightKg: 0,
  waistCircumferenceCm: undefined,
  currentMedications: [],
  hormonalContraceptives: false,
  contraceptiveType: '',
  recentlyStoppedContraceptive: false,
  monthsSinceStoppedContraceptive: undefined,
  eatingDisorderHistory: false,
  pregnancyStatus: 'not-pregnant',
};

const initialMenstrual: MenstrualData = {
  ageAtMenarche: 0,
  averageCycleLength: 28,
  cycleRegularity: 'regular',
  cycleVariabilityDays: undefined,
  periodDurationDays: 5,
  tracksOvulation: false,
  ovulationMethod: '',
  detectsOvulationRegularly: undefined,
  pregnancyHistory: { pregnancies: 0, miscarriages: 0, fertilityTreatments: false, troubleConceiving: false },
  hasHadUltrasound: false,
  ultrasoundResult: undefined,
  ultrasoundDate: '',
  amhTested: false,
  amhValue: undefined,
  amhUnit: 'ng/mL',
};

const initialHyperandrogenic: HyperandrogenicData = {
  hirsutism: { present: false, severity: 'none', locations: [] },
  acne: { present: false, severity: 'none', locations: [], persistent: false },
  hairThinning: { present: false, pattern: 'none' },
  voiceChanges: false,
  clitoromegaly: false,
  labResults: { hasElevatedAndrogens: 'unknown' },
};

const initialMetabolic: MetabolicData = {
  weightDifficulty: 'none',
  centralObesity: false,
  waistHipRatio: undefined,
  fastingGlucose: undefined,
  fastingInsulin: undefined,
  homaIr: undefined,
  hba1c: undefined,
  diabetesHistory: { prediabetes: false, type2Diabetes: false, gestationalDiabetes: false, insulinResistanceDiagnosis: false },
  diabetesMedications: [],
  lipidAbnormalities: false,
  highBloodPressure: false,
  priorWeightLossMeds: [],
  interestedInGlp1: false,
};

const initialMentalHealth: MentalHealthData = {
  anxietySymptoms: 'none',
  depressionSymptoms: 'none',
  sleepQuality: 'good',
  sleepDurationHours: 7,
  sleepApneaSymptoms: false,
  stressLevel: 'moderate',
  fatigueSeverity: 'none',
};

const initialInflammatory: InflammatoryData = {
  autoimmuneDiagnoses: [],
  thyroidDisorder: false,
  thyroidType: undefined,
  gutSymptoms: { ibs: false, bloating: false, constipation: false, diarrhea: false, foodSensitivities: [] },
  chronicFatigue: false,
  jointPain: false,
  frequentHeadaches: false,
  crpTested: false,
  crpValue: undefined,
  vitaminDTested: false,
  vitaminDValue: undefined,
};

const initialFamilyHistory: FamilyHistoryData = {
  pcosInFamily: false,
  pcosRelatives: [],
  diabetesInFamily: false,
  obesityInFamily: false,
  cardiovascularDiseaseInFamily: false,
  thyroidDisordersInFamily: false,
  infertilityInFamily: false,
};

const initialLifestyle: LifestyleData = {
  dietPattern: 'balanced',
  exerciseFrequency: 'moderate',
  exerciseType: [],
  smokingStatus: 'never',
  alcoholUse: 'occasional',
  endocrineDisruptorAwareness: false,
};

const initialGLP1Screening: GLP1ScreeningData = {
  giDiseaseHistory: false,
  pancreatitisHistory: false,
  severeGiCondition: false,
  thyroidCancerFamilyHistory: false,
  mtcMen2History: false,
  medicationsAffectedByGastricEmptying: [],
  confirmNotPregnant: false,
  confirmNotBreastfeeding: false,
};

// ============================================================================
// FORM COMPONENTS
// ============================================================================

interface FormFieldProps {
  label: string;
  description?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, description, required, error, children }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium mb-1" style={{ color: '#E6E9F0' }}>
      {label}
      {required && <span className="ml-1" style={{ color: '#ef4444' }}>*</span>}
    </label>
    {description && (
      <p className="text-xs mb-2" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>{description}</p>
    )}
    {children}
    {error && (
      <p className="mt-1 text-xs flex items-center gap-1" style={{ color: '#ef4444' }}>
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

interface RadioGroupProps {
  name: string;
  options: { value: string; label: string; description?: string }[];
  value: string;
  onChange: (value: string) => void;
}

const RadioGroup: React.FC<RadioGroupProps> = ({ name, options, value, onChange }) => (
  <div className="space-y-2">
    {options.map((option) => (
      <label
        key={option.value}
        className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
        style={{
          background: value === option.value ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
          border: value === option.value ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1"
          style={{ accentColor: '#2FFFC7' }}
        />
        <div>
          <span className="text-sm" style={{ color: '#E6E9F0' }}>{option.label}</span>
          {option.description && (
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>{option.description}</p>
          )}
        </div>
      </label>
    ))}
  </div>
);

interface CheckboxGroupProps {
  options: { value: string; label: string }[];
  values: string[];
  onChange: (values: string[]) => void;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ options, values, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {options.map((option) => (
      <label
        key={option.value}
        className="flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all"
        style={{
          background: values.includes(option.value) ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
          border: values.includes(option.value) ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <input
          type="checkbox"
          checked={values.includes(option.value)}
          onChange={(e) => {
            if (e.target.checked) {
              onChange([...values, option.value]);
            } else {
              onChange(values.filter((v) => v !== option.value));
            }
          }}
          style={{ accentColor: '#2FFFC7' }}
        />
        <span className="text-sm" style={{ color: '#E6E9F0' }}>{option.label}</span>
      </label>
    ))}
  </div>
);

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

interface SectionProps<T> {
  data: T;
  updateData: (data: Partial<T>) => void;
}

// Demographics Section
const DemographicsSection: React.FC<SectionProps<DemographicsData>> = ({ data, updateData }) => {
  const [useMetricHeight, setUseMetricHeight] = useState(false);
  const [useMetricWeight, setUseMetricWeight] = useState(false);
  const [feet, setFeet] = useState<number | ''>(data.heightCm ? Math.floor(data.heightCm / 30.48) : '');
  const [inches, setInches] = useState<number | ''>(data.heightCm ? Math.round((data.heightCm / 2.54) % 12) : '');
  const [lbs, setLbs] = useState<number | ''>(data.weightKg ? Math.round(data.weightKg * 2.20462) : '');

  const handleHeightImperial = (newFeet: number | '', newInches: number | '') => {
    setFeet(newFeet);
    setInches(newInches);
    const totalInches = ((newFeet || 0) * 12) + (newInches || 0);
    const cm = Math.round(totalInches * 2.54);
    updateData({ heightCm: cm > 0 ? cm : 0 });
  };

  const handleWeightImperial = (newLbs: number | '') => {
    setLbs(newLbs);
    const kg = newLbs ? Math.round(newLbs / 2.20462 * 10) / 10 : 0;
    updateData({ weightKg: kg });
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#E6E9F0',
    outline: 'none',
  };

  return (
    <div className="space-y-6">
      <FormField label="Age Range" required>
        <RadioGroup
          name="ageRange"
          value={data.ageRange}
          onChange={(value) => updateData({ ageRange: value })}
          options={[
            { value: '18-24', label: '18-24 years' },
            { value: '25-34', label: '25-34 years' },
            { value: '35-44', label: '35-44 years' },
            { value: '45-54', label: '45-54 years' },
            { value: '55+', label: '55+ years' },
          ]}
        />
      </FormField>

      <FormField label="Height" required>
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => setUseMetricHeight(false)}
            className="px-3 py-1.5 text-xs rounded-lg transition-all"
            style={{
              background: !useMetricHeight ? '#2FFFC7' : 'rgba(255, 255, 255, 0.02)',
              color: !useMetricHeight ? '#000' : 'rgba(255, 255, 255, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            ft/in
          </button>
          <button
            type="button"
            onClick={() => setUseMetricHeight(true)}
            className="px-3 py-1.5 text-xs rounded-lg transition-all"
            style={{
              background: useMetricHeight ? '#2FFFC7' : 'rgba(255, 255, 255, 0.02)',
              color: useMetricHeight ? '#000' : 'rgba(255, 255, 255, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            cm
          </button>
        </div>
        {useMetricHeight ? (
          <input
            type="number"
            value={data.heightCm || ''}
            onChange={(e) => updateData({ heightCm: Number(e.target.value) })}
            placeholder="e.g., 165"
            style={inputStyle}
          />
        ) : (
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="number"
                value={feet}
                onChange={(e) => handleHeightImperial(e.target.value ? Number(e.target.value) : '', inches)}
                placeholder="5"
                min="0"
                max="8"
                style={{ ...inputStyle, paddingRight: '40px' }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>ft</span>
            </div>
            <div className="flex-1 relative">
              <input
                type="number"
                value={inches}
                onChange={(e) => handleHeightImperial(feet, e.target.value ? Number(e.target.value) : '')}
                placeholder="6"
                min="0"
                max="11"
                style={{ ...inputStyle, paddingRight: '40px' }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>in</span>
            </div>
          </div>
        )}
      </FormField>

      <FormField label="Weight" required>
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => setUseMetricWeight(false)}
            className="px-3 py-1.5 text-xs rounded-lg transition-all"
            style={{
              background: !useMetricWeight ? '#2FFFC7' : 'rgba(255, 255, 255, 0.02)',
              color: !useMetricWeight ? '#000' : 'rgba(255, 255, 255, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            lbs
          </button>
          <button
            type="button"
            onClick={() => setUseMetricWeight(true)}
            className="px-3 py-1.5 text-xs rounded-lg transition-all"
            style={{
              background: useMetricWeight ? '#2FFFC7' : 'rgba(255, 255, 255, 0.02)',
              color: useMetricWeight ? '#000' : 'rgba(255, 255, 255, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            kg
          </button>
        </div>
        {useMetricWeight ? (
          <input
            type="number"
            value={data.weightKg || ''}
            onChange={(e) => updateData({ weightKg: Number(e.target.value) })}
            placeholder="e.g., 65"
            style={inputStyle}
          />
        ) : (
          <div className="relative">
            <input
              type="number"
              value={lbs}
              onChange={(e) => handleWeightImperial(e.target.value ? Number(e.target.value) : '')}
              placeholder="e.g., 140"
              style={{ ...inputStyle, paddingRight: '48px' }}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>lbs</span>
          </div>
        )}
      </FormField>

      <FormField label="Current Pregnancy Status" required>
        <RadioGroup
          name="pregnancyStatus"
          value={data.pregnancyStatus}
          onChange={(value) => updateData({ pregnancyStatus: value as DemographicsData['pregnancyStatus'] })}
          options={[
            { value: 'not-pregnant', label: 'Not pregnant' },
            { value: 'pregnant', label: 'Currently pregnant' },
            { value: 'breastfeeding', label: 'Currently breastfeeding' },
            { value: 'trying-to-conceive', label: 'Trying to conceive' },
          ]}
        />
      </FormField>

      <FormField label="Are you currently using hormonal contraceptives?">
        <RadioGroup
          name="hormonalContraceptives"
          value={data.hormonalContraceptives ? 'yes' : 'no'}
          onChange={(value) => updateData({ hormonalContraceptives: value === 'yes' })}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
      </FormField>

      <FormField label="Have you recently stopped hormonal contraceptives (within 12 months)?">
        <RadioGroup
          name="recentlyStoppedContraceptive"
          value={data.recentlyStoppedContraceptive ? 'yes' : 'no'}
          onChange={(value) => updateData({ recentlyStoppedContraceptive: value === 'yes' })}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
      </FormField>

      {data.recentlyStoppedContraceptive && (
        <FormField label="How many months ago did you stop?">
          <input
            type="number"
            value={data.monthsSinceStoppedContraceptive || ''}
            onChange={(e) => updateData({ monthsSinceStoppedContraceptive: Number(e.target.value) || undefined })}
            placeholder="e.g., 6"
            style={inputStyle}
          />
        </FormField>
      )}
    </div>
  );
};

// Menstrual Section
const MenstrualSection: React.FC<SectionProps<MenstrualData>> = ({ data, updateData }) => {
  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#E6E9F0',
    outline: 'none',
  };

  return (
    <div className="space-y-6">
      <FormField label="Age at first period (menarche)" required>
        <input
          type="number"
          value={data.ageAtMenarche || ''}
          onChange={(e) => updateData({ ageAtMenarche: Number(e.target.value) })}
          placeholder="e.g., 12"
          style={inputStyle}
        />
      </FormField>

      <FormField label="How would you describe your menstrual cycle regularity?" required>
        <RadioGroup
          name="cycleRegularity"
          value={data.cycleRegularity}
          onChange={(value) => updateData({ cycleRegularity: value as MenstrualData['cycleRegularity'] })}
          options={[
            { value: 'regular', label: 'Regular', description: 'Cycles are predictable (24-35 days)' },
            { value: 'irregular', label: 'Irregular', description: 'Cycles vary significantly or are unpredictable' },
            { value: 'variable', label: 'Highly Variable', description: '8+ days variation year to year' },
            { value: 'absent', label: 'Absent', description: 'Periods are absent for 3+ months' },
          ]}
        />
      </FormField>

      <FormField label="Average cycle length (days)" description="Count from day 1 of one period to day 1 of the next">
        <input
          type="number"
          value={data.averageCycleLength || ''}
          onChange={(e) => updateData({ averageCycleLength: Number(e.target.value) })}
          placeholder="e.g., 28"
          style={inputStyle}
        />
      </FormField>

      <FormField label="Do you track ovulation?">
        <RadioGroup
          name="tracksOvulation"
          value={data.tracksOvulation ? 'yes' : 'no'}
          onChange={(value) => updateData({ tracksOvulation: value === 'yes' })}
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes', description: 'Via temperature, OPKs, cervical mucus, etc.' },
          ]}
        />
      </FormField>

      {data.tracksOvulation && (
        <FormField label="Do you regularly detect ovulation?">
          <RadioGroup
            name="detectsOvulationRegularly"
            value={data.detectsOvulationRegularly === undefined ? '' : data.detectsOvulationRegularly ? 'yes' : 'no'}
            onChange={(value) => updateData({ detectsOvulationRegularly: value === 'yes' })}
            options={[
              { value: 'yes', label: 'Yes, most cycles' },
              { value: 'no', label: 'No or rarely' },
            ]}
          />
        </FormField>
      )}

      <FormField label="Have you had difficulty getting pregnant?">
        <RadioGroup
          name="troubleConceiving"
          value={data.pregnancyHistory.troubleConceiving ? 'yes' : 'no'}
          onChange={(value) => updateData({ 
            pregnancyHistory: { ...data.pregnancyHistory, troubleConceiving: value === 'yes' } 
          })}
          options={[
            { value: 'no', label: 'No / Not applicable' },
            { value: 'yes', label: 'Yes' },
          ]}
        />
      </FormField>

      <FormField label="Have you had a pelvic ultrasound?">
        <RadioGroup
          name="hasHadUltrasound"
          value={data.hasHadUltrasound ? 'yes' : 'no'}
          onChange={(value) => updateData({ hasHadUltrasound: value === 'yes' })}
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
          ]}
        />
      </FormField>

      {data.hasHadUltrasound && (
        <FormField label="What did the ultrasound show?">
          <RadioGroup
            name="ultrasoundResult"
            value={data.ultrasoundResult || ''}
            onChange={(value) => updateData({ ultrasoundResult: value as MenstrualData['ultrasoundResult'] })}
            options={[
              { value: 'normal', label: 'Normal ovaries' },
              { value: 'polycystic', label: 'Polycystic ovaries', description: '12+ follicles or increased ovarian volume' },
              { value: 'multi-follicular', label: 'Multi-follicular ovaries' },
              { value: 'unknown', label: "Don't know / Don't remember" },
            ]}
          />
        </FormField>
      )}
    </div>
  );
};

// Hyperandrogenic Section
const HyperandrogenicSection: React.FC<SectionProps<HyperandrogenicData>> = ({ data, updateData }) => (
  <div className="space-y-6">
    <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(47, 255, 199, 0.05)', border: '1px solid rgba(47, 255, 199, 0.2)' }}>
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
        <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          These questions assess signs of elevated androgens (male hormones), which is one of the 
          key diagnostic criteria for PCOS.
        </p>
      </div>
    </div>

    <FormField label="Do you have unwanted facial or body hair growth (hirsutism)?">
      <RadioGroup
        name="hirsutismPresent"
        value={data.hirsutism.present ? 'yes' : 'no'}
        onChange={(value) => updateData({ 
          hirsutism: { ...data.hirsutism, present: value === 'yes' } 
        })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes', description: 'Coarse, dark hair in male-pattern areas' },
        ]}
      />
    </FormField>

    {data.hirsutism.present && (
      <>
        <FormField label="How severe is the hair growth?">
          <RadioGroup
            name="hirsutismSeverity"
            value={data.hirsutism.severity}
            onChange={(value) => updateData({ 
              hirsutism: { ...data.hirsutism, severity: value as HyperandrogenicData['hirsutism']['severity'] } 
            })}
            options={[
              { value: 'mild', label: 'Mild', description: 'Occasional removal needed' },
              { value: 'moderate', label: 'Moderate', description: 'Regular removal needed (weekly)' },
              { value: 'severe', label: 'Severe', description: 'Daily removal needed or very noticeable' },
            ]}
          />
        </FormField>
        <FormField label="Where do you notice excess hair?">
          <CheckboxGroup
            options={[
              { value: 'upper-lip', label: 'Upper lip' },
              { value: 'chin', label: 'Chin' },
              { value: 'chest', label: 'Chest' },
              { value: 'abdomen', label: 'Abdomen' },
              { value: 'back', label: 'Back' },
              { value: 'thighs', label: 'Upper thighs' },
            ]}
            values={data.hirsutism.locations}
            onChange={(values) => updateData({ 
              hirsutism: { ...data.hirsutism, locations: values } 
            })}
          />
        </FormField>
      </>
    )}

    <FormField label="Do you have persistent acne?">
      <RadioGroup
        name="acnePresent"
        value={data.acne.present ? 'yes' : 'no'}
        onChange={(value) => updateData({ 
          acne: { ...data.acne, present: value === 'yes' } 
        })}
        options={[
          { value: 'no', label: 'No or occasional mild acne' },
          { value: 'yes', label: 'Yes, persistent acne' },
        ]}
      />
    </FormField>

    {data.acne.present && (
      <FormField label="How severe is your acne?">
        <RadioGroup
          name="acneSeverity"
          value={data.acne.severity}
          onChange={(value) => updateData({ 
            acne: { ...data.acne, severity: value as HyperandrogenicData['acne']['severity'] } 
          })}
          options={[
            { value: 'mild', label: 'Mild', description: 'Few spots' },
            { value: 'moderate', label: 'Moderate', description: 'Multiple spots, some inflammation' },
            { value: 'severe', label: 'Severe', description: 'Extensive, cystic, or scarring' },
          ]}
        />
      </FormField>
    )}

    <FormField label="Are you experiencing hair thinning on your scalp?">
      <RadioGroup
        name="hairThinningPresent"
        value={data.hairThinning.present ? 'yes' : 'no'}
        onChange={(value) => updateData({ 
          hairThinning: { ...data.hairThinning, present: value === 'yes' } 
        })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Have you ever had elevated testosterone or DHEA-S on a blood test?">
      <RadioGroup
        name="hasElevatedAndrogens"
        value={data.labResults.hasElevatedAndrogens === 'unknown' ? 'unknown' : data.labResults.hasElevatedAndrogens ? 'yes' : 'no'}
        onChange={(value) => updateData({ 
          labResults: { 
            ...data.labResults, 
            hasElevatedAndrogens: value === 'unknown' ? 'unknown' : value === 'yes' 
          } 
        })}
        options={[
          { value: 'no', label: 'No, labs were normal' },
          { value: 'yes', label: 'Yes, one or more were elevated' },
          { value: 'unknown', label: "I don't know / Never tested" },
        ]}
      />
    </FormField>
  </div>
);

// Metabolic Section
const MetabolicSection: React.FC<SectionProps<MetabolicData>> = ({ data, updateData }) => (
  <div className="space-y-6">
    <FormField label="How difficult is it for you to manage your weight?">
      <RadioGroup
        name="weightDifficulty"
        value={data.weightDifficulty}
        onChange={(value) => updateData({ weightDifficulty: value as MetabolicData['weightDifficulty'] })}
        options={[
          { value: 'none', label: 'No difficulty' },
          { value: 'mild', label: 'Mild difficulty' },
          { value: 'moderate', label: 'Moderate difficulty', description: 'Weight loss is slow despite effort' },
          { value: 'severe', label: 'Significant difficulty', description: 'Very hard to lose weight or gain easily' },
        ]}
      />
    </FormField>

    <FormField label="Do you carry weight primarily around your midsection?">
      <RadioGroup
        name="centralObesity"
        value={data.centralObesity ? 'yes' : 'no'}
        onChange={(value) => updateData({ centralObesity: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Have you been diagnosed with any of the following?" description="Select all that apply">
      <div className="space-y-2">
        {[
          { key: 'prediabetes', label: 'Pre-diabetes' },
          { key: 'type2Diabetes', label: 'Type 2 Diabetes' },
          { key: 'gestationalDiabetes', label: 'Gestational Diabetes (during pregnancy)' },
          { key: 'insulinResistanceDiagnosis', label: 'Insulin Resistance' },
        ].map((item) => (
          <label
            key={item.key}
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
            style={{
              background: data.diabetesHistory[item.key as keyof typeof data.diabetesHistory]
                ? 'rgba(47, 255, 199, 0.1)'
                : 'rgba(255, 255, 255, 0.02)',
              border: data.diabetesHistory[item.key as keyof typeof data.diabetesHistory]
                ? '1px solid rgba(47, 255, 199, 0.5)'
                : '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <input
              type="checkbox"
              checked={data.diabetesHistory[item.key as keyof typeof data.diabetesHistory]}
              onChange={(e) => updateData({
                diabetesHistory: { ...data.diabetesHistory, [item.key]: e.target.checked }
              })}
              style={{ accentColor: '#2FFFC7' }}
            />
            <span className="text-sm" style={{ color: '#E6E9F0' }}>{item.label}</span>
          </label>
        ))}
      </div>
    </FormField>

    <FormField label="Have you used any metabolic medications?" description="Select all that apply">
      <CheckboxGroup
        options={[
          { value: 'metformin', label: 'Metformin' },
          { value: 'ozempic', label: 'Ozempic (semaglutide)' },
          { value: 'wegovy', label: 'Wegovy' },
          { value: 'mounjaro', label: 'Mounjaro (tirzepatide)' },
          { value: 'other-glp1', label: 'Other GLP-1' },
        ]}
        values={data.diabetesMedications}
        onChange={(values) => updateData({ diabetesMedications: values })}
      />
    </FormField>

    <FormField label="Are you interested in learning about GLP-1 medications for weight/metabolic management?">
      <RadioGroup
        name="interestedInGlp1"
        value={data.interestedInGlp1 ? 'yes' : 'no'}
        onChange={(value) => updateData({ interestedInGlp1: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>
  </div>
);

// Mental Health Section
const MentalHealthSection: React.FC<SectionProps<MentalHealthData>> = ({ data, updateData }) => {
  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#E6E9F0',
    outline: 'none',
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(47, 255, 199, 0.05)', border: '1px solid rgba(47, 255, 199, 0.2)' }}>
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
          <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            The 2023 PCOS guidelines emphasize screening for mental health conditions, as they 
            commonly co-occur with PCOS.
          </p>
        </div>
      </div>

      <FormField label="Over the past 2 weeks, how often have you felt nervous, anxious, or on edge?">
        <RadioGroup
          name="anxietySymptoms"
          value={data.anxietySymptoms}
          onChange={(value) => updateData({ anxietySymptoms: value as MentalHealthData['anxietySymptoms'] })}
          options={[
            { value: 'none', label: 'Not at all' },
            { value: 'mild', label: 'Several days' },
            { value: 'moderate', label: 'More than half the days' },
            { value: 'severe', label: 'Nearly every day' },
          ]}
        />
      </FormField>

      <FormField label="Over the past 2 weeks, how often have you felt down, depressed, or hopeless?">
        <RadioGroup
          name="depressionSymptoms"
          value={data.depressionSymptoms}
          onChange={(value) => updateData({ depressionSymptoms: value as MentalHealthData['depressionSymptoms'] })}
          options={[
            { value: 'none', label: 'Not at all' },
            { value: 'mild', label: 'Several days' },
            { value: 'moderate', label: 'More than half the days' },
            { value: 'severe', label: 'Nearly every day' },
          ]}
        />
      </FormField>

      <FormField label="How would you rate your sleep quality?">
        <RadioGroup
          name="sleepQuality"
          value={data.sleepQuality}
          onChange={(value) => updateData({ sleepQuality: value as MentalHealthData['sleepQuality'] })}
          options={[
            { value: 'good', label: 'Good', description: 'Generally sleep well and feel rested' },
            { value: 'fair', label: 'Fair', description: 'Occasional issues' },
            { value: 'poor', label: 'Poor', description: 'Frequent trouble sleeping' },
            { value: 'very-poor', label: 'Very Poor', description: 'Significant sleep problems' },
          ]}
        />
      </FormField>

      <FormField label="On average, how many hours of sleep do you get per night?">
        <input
          type="number"
          step="0.5"
          value={data.sleepDurationHours || ''}
          onChange={(e) => updateData({ sleepDurationHours: Number(e.target.value) })}
          placeholder="e.g., 7"
          style={inputStyle}
        />
      </FormField>

      <FormField label="How would you rate your current stress level?">
        <RadioGroup
          name="stressLevel"
          value={data.stressLevel}
          onChange={(value) => updateData({ stressLevel: value as MentalHealthData['stressLevel'] })}
          options={[
            { value: 'low', label: 'Low' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'high', label: 'High' },
            { value: 'very-high', label: 'Very High' },
          ]}
        />
      </FormField>
    </div>
  );
};

// Inflammatory Section
const InflammatorySection: React.FC<SectionProps<InflammatoryData>> = ({ data, updateData }) => (
  <div className="space-y-6">
    <FormField label="Do you have any autoimmune conditions?" description="Select all that apply">
      <CheckboxGroup
        options={[
          { value: 'hashimotos', label: "Hashimoto's Thyroiditis" },
          { value: 'rheumatoid', label: 'Rheumatoid Arthritis' },
          { value: 'lupus', label: 'Lupus' },
          { value: 'celiac', label: 'Celiac Disease' },
          { value: 'psoriasis', label: 'Psoriasis' },
          { value: 'other', label: 'Other autoimmune' },
        ]}
        values={data.autoimmuneDiagnoses}
        onChange={(values) => updateData({ autoimmuneDiagnoses: values })}
      />
    </FormField>

    <FormField label="Do you have a thyroid disorder?">
      <RadioGroup
        name="thyroidDisorder"
        value={data.thyroidDisorder ? 'yes' : 'no'}
        onChange={(value) => updateData({ thyroidDisorder: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Do you experience gut/digestive symptoms?" description="Select all that apply">
      <div className="space-y-2">
        {[
          { key: 'ibs', label: 'IBS diagnosis or symptoms' },
          { key: 'bloating', label: 'Frequent bloating' },
          { key: 'constipation', label: 'Constipation' },
          { key: 'diarrhea', label: 'Diarrhea' },
        ].map((item) => (
          <label
            key={item.key}
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
            style={{
              background: data.gutSymptoms[item.key as keyof typeof data.gutSymptoms]
                ? 'rgba(47, 255, 199, 0.1)'
                : 'rgba(255, 255, 255, 0.02)',
              border: data.gutSymptoms[item.key as keyof typeof data.gutSymptoms]
                ? '1px solid rgba(47, 255, 199, 0.5)'
                : '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <input
              type="checkbox"
              checked={data.gutSymptoms[item.key as keyof typeof data.gutSymptoms] as boolean}
              onChange={(e) => updateData({
                gutSymptoms: { ...data.gutSymptoms, [item.key]: e.target.checked }
              })}
              style={{ accentColor: '#2FFFC7' }}
            />
            <span className="text-sm" style={{ color: '#E6E9F0' }}>{item.label}</span>
          </label>
        ))}
      </div>
    </FormField>

    <FormField label="Do you experience chronic fatigue?">
      <RadioGroup
        name="chronicFatigue"
        value={data.chronicFatigue ? 'yes' : 'no'}
        onChange={(value) => updateData({ chronicFatigue: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Do you experience joint pain?">
      <RadioGroup
        name="jointPain"
        value={data.jointPain ? 'yes' : 'no'}
        onChange={(value) => updateData({ jointPain: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>
  </div>
);

// Family History Section
const FamilyHistorySection: React.FC<SectionProps<FamilyHistoryData>> = ({ data, updateData }) => (
  <div className="space-y-6">
    <FormField label="Do any close relatives have PCOS?">
      <RadioGroup
        name="pcosInFamily"
        value={data.pcosInFamily ? 'yes' : 'no'}
        onChange={(value) => updateData({ pcosInFamily: value === 'yes' })}
        options={[
          { value: 'no', label: 'No / Unknown' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Family history of diabetes?">
      <RadioGroup
        name="diabetesInFamily"
        value={data.diabetesInFamily ? 'yes' : 'no'}
        onChange={(value) => updateData({ diabetesInFamily: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes (parent, sibling, or grandparent)' },
        ]}
      />
    </FormField>

    <FormField label="Family history of obesity?">
      <RadioGroup
        name="obesityInFamily"
        value={data.obesityInFamily ? 'yes' : 'no'}
        onChange={(value) => updateData({ obesityInFamily: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Family history of heart disease or cardiovascular problems?">
      <RadioGroup
        name="cardiovascularDiseaseInFamily"
        value={data.cardiovascularDiseaseInFamily ? 'yes' : 'no'}
        onChange={(value) => updateData({ cardiovascularDiseaseInFamily: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>

    <FormField label="Family history of thyroid disorders?">
      <RadioGroup
        name="thyroidDisordersInFamily"
        value={data.thyroidDisordersInFamily ? 'yes' : 'no'}
        onChange={(value) => updateData({ thyroidDisordersInFamily: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </FormField>
  </div>
);

// Lifestyle Section
const LifestyleSection: React.FC<SectionProps<LifestyleData>> = ({ data, updateData }) => (
  <div className="space-y-6">
    <FormField label="How would you describe your typical diet?">
      <RadioGroup
        name="dietPattern"
        value={data.dietPattern}
        onChange={(value) => updateData({ dietPattern: value as LifestyleData['dietPattern'] })}
        options={[
          { value: 'high-sugar', label: 'High in sugar/processed foods' },
          { value: 'processed', label: 'Mostly processed/convenience foods' },
          { value: 'balanced', label: 'Balanced (whole foods, varied)' },
          { value: 'low-carb', label: 'Low-carb/keto' },
          { value: 'vegetarian', label: 'Vegetarian/vegan' },
          { value: 'other', label: 'Other' },
        ]}
      />
    </FormField>

    <FormField label="How often do you exercise?">
      <RadioGroup
        name="exerciseFrequency"
        value={data.exerciseFrequency}
        onChange={(value) => updateData({ exerciseFrequency: value as LifestyleData['exerciseFrequency'] })}
        options={[
          { value: 'sedentary', label: 'Sedentary', description: 'Little to no regular exercise' },
          { value: 'light', label: 'Light', description: '1-2 times per week' },
          { value: 'moderate', label: 'Moderate', description: '3-4 times per week' },
          { value: 'vigorous', label: 'Vigorous', description: '5+ times per week' },
        ]}
      />
    </FormField>

    <FormField label="Smoking status">
      <RadioGroup
        name="smokingStatus"
        value={data.smokingStatus}
        onChange={(value) => updateData({ smokingStatus: value as LifestyleData['smokingStatus'] })}
        options={[
          { value: 'never', label: 'Never smoked' },
          { value: 'former', label: 'Former smoker' },
          { value: 'current', label: 'Current smoker' },
        ]}
      />
    </FormField>

    <FormField label="Alcohol consumption">
      <RadioGroup
        name="alcoholUse"
        value={data.alcoholUse}
        onChange={(value) => updateData({ alcoholUse: value as LifestyleData['alcoholUse'] })}
        options={[
          { value: 'none', label: 'None' },
          { value: 'occasional', label: 'Occasional (few times/month)' },
          { value: 'moderate', label: 'Moderate (few times/week)' },
          { value: 'heavy', label: 'Heavy (daily)' },
        ]}
      />
    </FormField>
  </div>
);

// Lab Upload Section (Simplified)
const LabUploadSection: React.FC<{ onLabsUploaded: (labs: NimCoreLabPanel) => void }> = () => (
  <div className="space-y-6">
    <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(47, 255, 199, 0.05)', border: '1px solid rgba(47, 255, 199, 0.2)' }}>
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
        <div>
          <p className="text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            <strong style={{ color: '#E6E9F0' }}>Optional but recommended:</strong> Upload your lab results 
            for more accurate phenotype and endotype classification.
          </p>
          <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Lab analysis integration coming soon. For now, please proceed with the questionnaire.
          </p>
        </div>
      </div>
    </div>

    <p className="text-xs text-center" style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
      This section is optional. You can skip it if you don't have lab results available.
    </p>
  </div>
);

// GLP-1 Screening Section
const GLP1ScreeningSection: React.FC<SectionProps<GLP1ScreeningData>> = ({ data, updateData }) => (
  <div className="space-y-6">
    <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.2)' }}>
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#f97316' }} />
        <div>
          <p className="text-sm mb-2" style={{ color: '#E6E9F0' }}>
            <strong>GLP-1 Safety Screening</strong>
          </p>
          <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            These questions help determine if GLP-1 medications might be appropriate for you. 
            This does not replace medical consultation.
          </p>
        </div>
      </div>
    </div>

    <FormField label="Do you have a history of pancreatitis?">
      <RadioGroup
        name="pancreatitisHistory"
        value={data.pancreatitisHistory ? 'yes' : 'no'}
        onChange={(value) => updateData({ pancreatitisHistory: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes', description: 'GLP-1s are contraindicated with pancreatitis history' },
        ]}
      />
    </FormField>

    <FormField label="Do you have severe gastrointestinal disease?">
      <RadioGroup
        name="severeGiCondition"
        value={data.severeGiCondition ? 'yes' : 'no'}
        onChange={(value) => updateData({ severeGiCondition: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes', description: 'e.g., gastroparesis, inflammatory bowel disease' },
        ]}
      />
    </FormField>

    <FormField label="Do you or a close relative have a history of medullary thyroid carcinoma (MTC) or MEN2 syndrome?">
      <RadioGroup
        name="mtcMen2History"
        value={data.mtcMen2History ? 'yes' : 'no'}
        onChange={(value) => updateData({ mtcMen2History: value === 'yes' })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes', description: 'GLP-1s carry boxed warning for thyroid C-cell tumors' },
        ]}
      />
    </FormField>

    <FormField label="Please confirm you are NOT currently pregnant">
      <RadioGroup
        name="confirmNotPregnant"
        value={data.confirmNotPregnant ? 'yes' : 'no'}
        onChange={(value) => updateData({ confirmNotPregnant: value === 'yes' })}
        options={[
          { value: 'yes', label: 'I confirm I am NOT pregnant' },
          { value: 'no', label: 'I am pregnant or may be pregnant' },
        ]}
      />
    </FormField>

    <FormField label="Please confirm you are NOT currently breastfeeding">
      <RadioGroup
        name="confirmNotBreastfeeding"
        value={data.confirmNotBreastfeeding ? 'yes' : 'no'}
        onChange={(value) => updateData({ confirmNotBreastfeeding: value === 'yes' })}
        options={[
          { value: 'yes', label: 'I confirm I am NOT breastfeeding' },
          { value: 'no', label: 'I am currently breastfeeding' },
        ]}
      />
    </FormField>
  </div>
);

// ============================================================================
// MAIN QUIZ COMPONENT
// ============================================================================

const PCOSQuiz: React.FC = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [demographics, setDemographics] = useState<DemographicsData>(initialDemographics);
  const [menstrual, setMenstrual] = useState<MenstrualData>(initialMenstrual);
  const [hyperandrogenic, setHyperandrogenic] = useState<HyperandrogenicData>(initialHyperandrogenic);
  const [metabolic, setMetabolic] = useState<MetabolicData>(initialMetabolic);
  const [mentalHealth, setMentalHealth] = useState<MentalHealthData>(initialMentalHealth);
  const [inflammatory, setInflammatory] = useState<InflammatoryData>(initialInflammatory);
  const [familyHistory, setFamilyHistory] = useState<FamilyHistoryData>(initialFamilyHistory);
  const [lifestyle, setLifestyle] = useState<LifestyleData>(initialLifestyle);
  const [glp1Screening, setGlp1Screening] = useState<GLP1ScreeningData>(initialGLP1Screening);
  const [labData, setLabData] = useState<NimCoreLabPanel | undefined>(undefined);

  const sections = [
    { id: 'demographics', title: 'About You', component: <DemographicsSection data={demographics} updateData={(d) => setDemographics({ ...demographics, ...d })} /> },
    { id: 'menstrual', title: 'Menstrual Health', component: <MenstrualSection data={menstrual} updateData={(d) => setMenstrual({ ...menstrual, ...d })} /> },
    { id: 'hyperandrogenic', title: 'Hormonal Symptoms', component: <HyperandrogenicSection data={hyperandrogenic} updateData={(d) => setHyperandrogenic({ ...hyperandrogenic, ...d })} /> },
    { id: 'metabolic', title: 'Metabolic Health', component: <MetabolicSection data={metabolic} updateData={(d) => setMetabolic({ ...metabolic, ...d })} /> },
    { id: 'mentalHealth', title: 'Mental Wellness', component: <MentalHealthSection data={mentalHealth} updateData={(d) => setMentalHealth({ ...mentalHealth, ...d })} /> },
    { id: 'inflammatory', title: 'Inflammation', component: <InflammatorySection data={inflammatory} updateData={(d) => setInflammatory({ ...inflammatory, ...d })} /> },
    { id: 'familyHistory', title: 'Family History', component: <FamilyHistorySection data={familyHistory} updateData={(d) => setFamilyHistory({ ...familyHistory, ...d })} /> },
    { id: 'lifestyle', title: 'Lifestyle', component: <LifestyleSection data={lifestyle} updateData={(d) => setLifestyle({ ...lifestyle, ...d })} /> },
    { id: 'labUpload', title: 'Lab Results', component: <LabUploadSection onLabsUploaded={(labs) => setLabData(labs)} /> },
    { id: 'glp1Screening', title: 'Treatment Screening', component: <GLP1ScreeningSection data={glp1Screening} updateData={(d) => setGlp1Screening({ ...glp1Screening, ...d })} /> },
  ];

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const quizData: PCOSQuizData = {
      demographics,
      menstrual,
      hyperandrogenic,
      metabolic,
      mentalHealth,
      inflammatory,
      familyHistory,
      lifestyle,
      glp1Screening,
      labData,
      uploadedLabFile: !!labData,
    };

    try {
      // Calculate results locally
      const result = calculatePCOSAssessment(quizData);
      
      // Store result in sessionStorage for results page
      sessionStorage.setItem('pcosResult', JSON.stringify({ result }));
      
      // Navigate to results
      navigate('/pcos/results');
    } catch (error) {
      console.error('Error calculating results:', error);
      alert('There was an error processing your quiz. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-6" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, #131a2b 100%)' }}>
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Section {currentSection + 1} of {sections.length}
            </span>
            <span className="text-sm" style={{ color: '#2FFFC7' }}>
              {Math.round(((currentSection + 1) / sections.length) * 100)}% complete
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${((currentSection + 1) / sections.length) * 100}%`,
                background: 'linear-gradient(90deg, #2FFFC7, #3B82F6)',
              }}
            />
          </div>
        </div>

        {/* Section Navigation Pills */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8 -mx-6 px-6" style={{ scrollbarWidth: 'none' }}>
          {sections.map((section, i) => (
            <button
              key={section.id}
              onClick={() => setCurrentSection(i)}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all"
              style={{
                background: i === currentSection
                  ? '#2FFFC7'
                  : i < currentSection
                  ? 'rgba(47, 255, 199, 0.2)'
                  : 'rgba(255, 255, 255, 0.05)',
                color: i === currentSection
                  ? '#000'
                  : i < currentSection
                  ? '#2FFFC7'
                  : 'rgba(255, 255, 255, 0.4)',
                border: i === currentSection
                  ? 'none'
                  : i < currentSection
                  ? '1px solid rgba(47, 255, 199, 0.3)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                fontWeight: i === currentSection ? 600 : 400,
              }}
            >
              {i < currentSection && <CheckCircle className="w-3 h-3" />}
              {section.title}
            </button>
          ))}
        </div>

        {/* Section Content */}
        <div 
          className="p-8 rounded-2xl"
          style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
            {sections[currentSection].title}
          </h2>
          <p className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            {QUIZ_SECTIONS[currentSection]?.description}
          </p>
          
          {/* Why We Ask */}
          {QUIZ_SECTIONS[currentSection]?.whyWeAsk && (
            <div className="mb-8 p-4 rounded-xl" style={{ background: 'rgba(47, 255, 199, 0.05)', border: '1px solid rgba(47, 255, 199, 0.2)' }}>
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: '#2FFFC7' }}>Why we ask</p>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {QUIZ_SECTIONS[currentSection].whyWeAsk}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {sections[currentSection].component}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePrev}
            disabled={currentSection === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-xl transition-all"
            style={{
              background: currentSection === 0 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.05)',
              color: currentSection === 0 ? 'rgba(255, 255, 255, 0.3)' : '#E6E9F0',
              cursor: currentSection === 0 ? 'not-allowed' : 'pointer',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          {currentSection < sections.length - 1 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105"
              style={{
                background: '#2FFFC7',
                color: '#000',
              }}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105 disabled:opacity-50"
              style={{
                background: '#2FFFC7',
                color: '#000',
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Get My Results
                  <CheckCircle className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-center mt-8" style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
          This assessment is for educational purposes only and does not constitute medical advice. 
          Always consult a healthcare professional for diagnosis and treatment.
        </p>
      </div>
    </div>
  );
};

export default PCOSQuiz;
